import React, { useState, useEffect, useMemo } from "react";
import EmptyDataAlert from "../../Snippets/TableCustomHooks/EmptyDataAlert";
import Loader from "../../Snippets/Loader";
import Table from "../../Snippets/TableCustomHooks/Table";
import { Button1, Button3, Button4 } from "../../Snippets/Button";
import SearchBar from "../../Snippets/TableCustomHooks/SearchBar";
import Pagination from "../../Snippets/TableCustomHooks/Pagination";
import IndividualAction from "./Individual/IndividualAction";
import OrganizationAction from "./Organization/OrganizationAction";
import axiosInstance from "../../../Middleware/AxiosInstanceInterceptor";

const CustomerTable = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedRole, setSelectedRole] = useState("user");
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [openForm, setOpenForm] = useState("");

  // ===============================
  // 🔹 Fetch Users
  // ===============================
  const fetchUsers = async () => {
    try {
      setIsLoading(true);

      const body = {
        role: selectedRole,
        search: debouncedSearch || "",
      };

      const res = await axiosInstance.post("/user/userlist", body, {
        params: { page, limit },
        headers: {
          "module-id": selectedRole === "user" ? "user" : "entity",
        },
      });

      setUserData(res?.data?.data || []);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.log("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ===============================
  // 🔹 Debounce Search
  // ===============================
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    fetchUsers();
  }, [selectedRole, debouncedSearch, page]);

  // ===============================
  // 🔹 Table Columns
  // ===============================
  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        id: "serial",
        Cell: ({ row }) => (page - 1) * limit + row.index + 1,
      },
      {
        Header: "Name",
        Cell: ({ row }) => {
          const d = row.original;
          if (selectedRole === "entity") {
            return d?.name || "-";
          }
          return `${d?.first_name || ""} ${d?.last_name || ""}`.trim() || "-";
        },
      },
      {
        Header: "Email",
        Cell: ({ row }) => row.original?.email || "-",
      },
      {
        Header: "Phone",
        Cell: ({ row }) => row.original?.personal?.phone_number || "-",
      },
      {
        Header: "CUID",
        Cell: ({ row }) =>
          row.original?.user_cdid || row.original?.organization_cdid || "-",
      },
      {
        Header: "Type",
        Cell: () => (selectedRole === "entity" ? "Organization" : "Individual"),
      },
      {
        Header: "Action",
        Cell: () => (
          <div className="flex gap-3">
            <Button3 label="Edit" />
            <Button4 label="Delete" />
          </div>
        ),
      },
    ],
    [selectedRole, page],
  );

  const data = useMemo(() => userData, [userData]);

  // ===============================
  // 🔹 UI
  // ===============================
  return (
    <>
      <div className="ml-0 mt-20 md:mt-28 lg:ml-64 bg-gray-100 min-h-screen px-4 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          {/* Header */}
          <h1 className="text-2xl font-semibold mb-6">Customer Management</h1>

          {/* Role Switch */}
          <div className="flex flex-wrap gap-4 mb-4">
            <button
              onClick={() => {
                setSelectedRole("user");
                setPage(1);
              }}
              className={`px-5 py-2 rounded-md border transition ${
                selectedRole === "user"
                  ? "bg-blue-600 text-white"
                  : "bg-white border-gray-300"
              }`}
            >
              Individual
            </button>

            <button
              onClick={() => {
                setSelectedRole("entity");
                setPage(1);
              }}
              className={`px-5 py-2 rounded-md border transition ${
                selectedRole === "entity"
                  ? "bg-blue-600 text-white"
                  : "bg-white border-gray-300"
              }`}
            >
              Organization
            </button>
          </div>

          {/* Search + Add */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="w-full md:w-1/2">
              <SearchBar
                placeholder="Search here..."
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                handleSearch={(e) => setSearchValue(e.target.value)}
              />
            </div>

            <Button1
              onClick={() => {
                setOpenForm(selectedRole);
                setIsActionOpen(true);
              }}
              label="Add Customer +"
            />
          </div>

          {/* Table Section */}
          <div className="relative overflow-x-auto">
            {isLoading && (
              <div className="absolute inset-0 backdrop-blur-sm bg-white/60 flex items-center justify-center rounded-xl z-20 transition-all duration-300">
                <Loader text="Fetching customers..." />
              </div>
            )}

            {userData?.length > 0 ? (
              <>
                <Table columns={columns} data={data} />

                <div className="mt-6 flex justify-center">
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    hasPrevPage={page > 1}
                    hasNextPage={page < totalPages}
                    goToPrevPage={() => setPage((p) => Math.max(1, p - 1))}
                    goToNextPage={() =>
                      setPage((p) => (p < totalPages ? p + 1 : p))
                    }
                  />
                </div>
              </>
            ) : (
              !isLoading && <EmptyDataAlert title="No Data Found" />
            )}
          </div>
        </div>
      </div>

      {/* Forms */}
      {openForm === "user" && (
        <IndividualAction
          isPreviewOpen={isActionOpen}
          handleClose={() => {
            setIsActionOpen(false);
            fetchUsers();
          }}
        />
      )}

      {openForm === "entity" && (
        <OrganizationAction
          isPreviewOpen={isActionOpen}
          handleClose={() => {
            setIsActionOpen(false);
            fetchUsers();
          }}
        />
      )}
    </>
  );
};

export default CustomerTable;
